<template>
  <div>
    <v-btn
      id="cart-button"
      :disabled="selectResult.length == 0"
      @click="viewCart()"
      color="primary"
      outlined
    >
      View Cart ({{ cartSize }})
    </v-btn>

    <v-dialog v-model="dialog" @input="syncCart" max-width="1000">
      <v-card>
        <v-card-title class="text-h5" style="background-color: rgb(57, 53, 53)">
          Order Cart
          <v-spacer></v-spacer>
          <v-btn icon @click="dialog = false">
            <v-icon large>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-row no-gutters>
          <v-col>
            <v-container>
              <span class="pl-4"> Order Parameters </span>
              <order-params :show_acquisitions="false" ref="orderParams" />
            </v-container>
          </v-col>
          <v-col>
            <v-container>
              <spinner v-if="estimating" small>Calculating estimate...</spinner>
              <v-alert v-else-if="limitExceededError" text dense type="error">{{
                limitExceededError
              }}</v-alert>
              <span class="pl-4" v-else>
                Estimated Order Total: ${{ totalEstimatedCost }}
              </span>
              <v-responsive
                class="overflow-y-auto"
                min-height="600"
                max-height="600"
              >
                <v-list-group :value="false">
                  <template v-slot:activator>
                    <v-list-item-title style="font-size: 13px"
                      >Pricing Details</v-list-item-title
                    >
                  </template>
                  <v-list-item>
                    <v-tabs style="border: 1px solid #4c4b48">
                      <v-tab>Estimated Cost</v-tab>
                      <v-tab-item>
                        <v-data-table
                          :loading="estimating"
                          loading-text="Calculating Estimate"
                          :headers="cost_headers"
                          :items="cost_estimate"
                          hide-default-footer
                          dense
                        >
                        </v-data-table>
                      </v-tab-item>
                      <v-tab>Estimated Area</v-tab>
                      <v-tab-item>
                        <v-data-table
                          :loading="estimating"
                          loading-text="Calculating Estimate"
                          :headers="area_headers"
                          :items="area_estimate"
                          hide-default-footer
                          dense
                        >
                        </v-data-table>
                      </v-tab-item>
                    </v-tabs>
                  </v-list-item>
                </v-list-group>
              </v-responsive>
            </v-container>
          </v-col>
        </v-row>

        <v-divider></v-divider>

        <v-card-actions>
          <v-container>
            <v-row>
              <v-col>
                <span> {{ cartSize }} tiles selected </span>
              </v-col>
              <v-col cols="auto">
                <v-row>
                  <v-col>
                    <v-btn outlined @click="viewAllTiles()">
                      <span> View All Tiles </span>
                    </v-btn>
                  </v-col>
                  <v-col>
                    <v-btn
                      outlined
                      :disabled="cartSize == numberOfTiles"
                      @click="addAllToCart()"
                    >
                      Add all to cart
                    </v-btn>
                  </v-col>
                  <v-col>
                    <v-btn
                      outlined
                      :disabled="cartSize == 0"
                      @click="emptyCart()"
                    >
                      Empty Cart
                    </v-btn>
                  </v-col>
                  <v-col>
                    <v-btn
                      id="place-order-button"
                      color="primary"
                      outlined
                      :disabled="isOrderInvalid()"
                      @click="placeOrder()"
                    >
                      <spinner v-if="pending" small> Ordering... </spinner>
                      <span v-else> Place Order </span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Spinner from "@/components/shared/Spinner";
import OrderParams from "@/components/order/OrderParams.vue";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import { eventBus } from "@/main.ts";
import { mapGetters } from "vuex";
import orderApi from "@/api/order";
import _ from "lodash";
const cartStore = createNamespacedHelpers("shoppingCart");
const uiStore = createNamespacedHelpers("ui");

export default {
  setup() {
    const { addToCart } = cartStore.useMutations(["addToCart"]);
    const { emptyStore } = cartStore.useMutations(["emptyStore"]);
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { addToCart, emptyStore, SHOW_SNACKBAR };
  },
  props: ["selectResult", "select_id", "select_aoi_obj"],
  data() {
    return {
      dialog: false,
      pending: false,
      estimating: false,
      response: null,
      cartSelection: this.$store.state.shoppingCart.cart,
      limitExceededError: null,
      default_cost_usage: {
        fresh_imagery_cost: 0,
        standard_imagery_cost: 0,
        total_imagery_cost: 0,
        training_imagery_cost: 0,
      },
      cost_estimate: [this.default_cost_usage],
      default_area_usage: {
        fresh_imagery_sqkm: 0,
        standard_imagery_sqkm: 0,
        total_imagery_sqkm: 0,
        training_imagery_sqkm: 0,
      },
      area_estimate: [this.default_area_usage],
      area_headers: [
        { text: "Fresh (SqKm)", value: "fresh_imagery_sqkm", sortable: false },
        {
          text: "Standard (SqKm)",
          value: "standard_imagery_sqkm",
          sortable: false,
        },
        {
          text: "Training (SqKm)",
          value: "training_imagery_sqkm",
          sortable: false,
        },
        { text: "Total (SqKm)", value: "total_imagery_sqkm", sortable: false },
      ],
      cost_headers: [
        { text: "Fresh ($)", value: "fresh_imagery_cost", sortable: false },
        {
          text: "Standard ($)",
          value: "standard_imagery_cost",
          sortable: false,
        },
        {
          text: "Training ($)",
          value: "training_imagery_cost",
          sortable: false,
        },
        { text: "Total ($)", value: "total_imagery_cost", sortable: false },
      ],
    };
  },
  components: { OrderParams, Spinner },
  computed: {
    ...mapGetters(["user_details", "account_details"]),
    numberOfTiles() {
      let count = 0;
      this.selectResult.forEach((cell) => {
        count += cell.children.length;
      });
      return count;
    },
    cartSize() {
      return this.$store.state.shoppingCart.cart.length;
    },
    totalEstimatedCost() {
      if (this.cost_estimate[0]) {
        return this.cost_estimate[0].total_imagery_cost;
      } else if (this.limitExceededError) {
        /* Sample limitExceededError
         *
         * "Forbidden: This order cannot be placed because it exceeds the remaining allotted dollars for this user in one or more image age categories. 0 dollars available; estimated 14 dollars in this order."
         */
        let regex = /\d+/g;
        let lastNumber, match;

        // get the last number in the string
        do {
          match = regex.exec(this.limitExceededError);
          if (match) {
            lastNumber = match;
          }
        } while (match != null);

        return lastNumber && lastNumber.length > 0 ? lastNumber[0] : "";
      }

      return "";
    },
    is_internal() {
      if (this.account_details) {
        return this.account_details.internal;
      }
      return false;
    },
  },
  watch: {
    dialog: {
      immediate: true,
      deep: true,
      handler() {
        this.cartSelection = this.$store.state.shoppingCart.cart;
        this.getUsageEstimate();
      },
    },
  },
  methods: {
    viewCart() {
      this.dialog = true;
    },
    viewAllTiles() {
      this.dialog = false;
      this.$emit("view_all_tiles");
    },
    syncCart(e) {
      // send event to cell results component to sync checkbox selections
      this.cartSelection = this.$store.state.shoppingCart.cart;
      if (!e) {
        eventBus.$emit("syncCart", this.cartSelection);
      }
    },
    addAllToCart() {
      this.selectResult.forEach((cell) => {
        cell.children.forEach((tile) => {
          this.addToCart(tile);
        });
      });
      this.cartSelection = this.$store.state.shoppingCart.cart;
      eventBus.$emit("syncCart", this.cartSelection);
      this.getUsageEstimate();
    },
    emptyCart() {
      this.cartSelection = [];
      this.emptyStore();
      eventBus.$emit("syncCart", this.cartSelection);
      this.getUsageEstimate();
    },
    updateCart(e) {
      this.emptyStore();
      this.cartSelection.forEach((element) => {
        this.addToCart(element);
      });
      this.getUsageEstimate();
    },
    formatAcquisitions() {
      let acquisitions = [];
      let uniqueAcqs = new Set();
      this.cartSelection.forEach((tile) => {
        uniqueAcqs.add(tile.name);
      });
      uniqueAcqs.forEach((acq) => {
        acquisitions.push({
          id: acq,
          cells: [],
        });
      });
      this.cartSelection.forEach((tile) => {
        acquisitions.forEach((acq) => {
          if (tile.name == acq.id) {
            acq.cells.push(tile.cell);
          }
        });
      });
      return acquisitions;
    },
    isOrderInvalid() {
      if (
        this.cartSize == 0 ||
        (this.$refs.orderParams &&
          !this.$refs.orderParams.orderParamsValid()) ||
        this.limitExceededError
      ) {
        return true;
      } else {
        return false;
      }
    },
    formatNumber(usage) {
      Object.entries(usage[0]).forEach(([key, value]) => {
        if (typeof value === "number") {
          usage[0][key] = value.toLocaleString();
        }
      });
      return usage;
    },
    getUsageEstimate() {
      this.limitExceededError = null;

      if (this.cartSize > 0) {
        this.placeOrder(true);
      } else {
        this.cost_estimate = [this.default_cost_usage];
        this.area_estimate = [this.default_area_usage];
      }
    },
    async placeOrder(estimate = false) {
      var params = {};

      if (estimate) {
        this.estimating = true;
        params = {
          dry_run: true,
          output_config: {
            amazon_s3: {
              bucket: "cb-ard-policy",
              prefix: "dry_run",
            },
          },
        };
        params = { ...params, ...this.select_aoi_obj };
      } else {
        this.pending = true;
        const model = this.$refs.orderParams.model;
        const emailNotification = this.$refs.orderParams.emailNotification;
        const snsNotification = this.$refs.orderParams.snsNotification;
        let output_config = {};
        output_config[this.$refs.orderParams.selectedPlatform] =
          this.$refs.orderParams[this.$refs.orderParams.selectedPlatform];

        if ("azure_blob_storage" in output_config) {
          if (!output_config.azure_blob_storage.credentials_id) {
            delete output_config.azure_blob_storage.credentials_id;
          } else if (!output_config.azure_blob_storage.sas_url) {
            delete output_config.azure_blob_storage.sas_url;
          }
        } else if ("google_cloud_storage" in output_config) {
          if (!output_config.google_cloud_storage.credentials_id) {
            delete output_config.google_cloud_storage.credentials_id;
          } else if (!output_config.google_cloud_storage.service_credentials) {
            delete output_config.google_cloud_storage.service_credentials;
          }
        }
        let settings = model.settings;
        if (!this.is_internal) {
          [
            "visual",
            "ms_analytic",
            "pan_analytic",
            "data_mask",
            "building_mask",
            "p3dr",
          ].forEach((setting) => {
            delete settings[setting];
          });
        }

        var metadata = this.$refs.orderParams.formatOrderMetadata();
        metadata.ui = true;
        metadata.select_id = this.select_id;

        params = {
          bundle_adjust: model.bundleAdjust,
          dry_run: model.dryRun,
          output_config: output_config,
          metadata: metadata,
          settings: settings,
        };
        params = { ...params, ...this.select_aoi_obj };

        let notifications = [];
        if (emailNotification.address) {
          notifications.push(emailNotification);
        }
        if (snsNotification.topic_arn) {
          notifications.push(snsNotification);
        }
        if (notifications.length > 0) {
          params.notifications = notifications;
        }
      }

      params.acquisitions = this.formatAcquisitions();
      this.limitExceededError = null;

      orderApi
        .place_order(params, estimate)
        .then((response) => {
          this.response = response;
          // logic for actual order
          if (
            this.response &&
            this.response.data &&
            this.response.data.id &&
            !estimate
          ) {
            this.$router.push({
              name: "order-details",
              params: {
                account_id: this.user_details.account_id,
                order_id: this.response.data.id,
              },
            });
            this.SHOW_SNACKBAR({
              text: `Order ${this.response.data.id} successfully placed.`,
              color: "success",
            });
          }
          // logic for dry run order
          else if (
            this.response &&
            this.response.data &&
            this.$refs.orderParams.model.dryRun &&
            !estimate
          ) {
            this.SHOW_SNACKBAR({
              text: "Order dry run was successful.",
              color: "success",
            });
            this.cost_estimate = this.formatNumber([
              this.response.data.usage.cost,
            ]);
            this.area_estimate = this.formatNumber([
              this.response.data.usage.area,
            ]);
            this.estimating = false;
            this.pending = false;
          }
          // logic for estimating usage
          else if (this.response && this.response.data && estimate) {
            this.cost_estimate = this.formatNumber([
              this.response.data.usage.cost,
            ]);
            this.area_estimate = this.formatNumber([
              this.response.data.usage.area,
            ]);
            this.estimating = false;
            this.pending = false;
          } else {
            let errorMsg = `Unexpected response recieved - ${JSON.stringify(
              this.response
            )}`;

            if (this.response.data && this.response.data.error_message) {
              if (
                this.response.data.error_message.Error &&
                this.response.data.error_message.Cause
              ) {
                errorMsg = `${this.response.data.error_message.Error} - ${this.response.data.error_message.Cause}`;
              } else {
                errorMsg = JSON.stringify(this.response.data.error_message);
              }
            }

            throw new Error(errorMsg);
          }
        })
        .catch((error) => {
          this.estimating = false;
          this.pending = false;

          if (
            _.get(error, "response.data.code", null) === "dollar_limit_exceeded"
          ) {
            this.limitExceededError = _.get(
              error,
              "response.data.message",
              null
            );
          }
        });
    },
  },
};
</script>
